import { Box } from "@mui/material"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/system"
import { ConventionLocationStyle } from "app-constants/constants"
import { createURL_SSR, ROUTE } from "app-constants/routing"
import { makeStyles } from "tss-react/mui-compat"
import Launchpad from "components/Launchpad/Launchpad"
import { SponsorsList } from "components/SponsorsList"
import { ActivityIndicator } from "components/activity-indicator"
import MainLayout from "components/main-layout"
import LaunchpadContext from "contexts/LaunchpadContext"
import useHeadingStyles from "hooks/useHeadingStyles"
import buildServerSidePage from "lib/build-server-side-page"
import buildServerSideProps from "lib/build-server-side-props"
import applyCache from "lib/mw/apply-cache"
import applyCurrentServerContext from "lib/mw/apply-current-server-context"
import applyNextSeo from "lib/mw/apply-next-seo"
import applyRequiredProps from "lib/mw/apply-required-props"
import applyTitle from "lib/mw/apply-title"
import postActiveAuthenticatedVisitors from "lib/postActiveAuthenticatedVisitors"
import useCurrent from "lib/use-current"
import ActiveVisitorCount from "models/convention-api/v1/ActiveVisitorCount"
import Convention from "models/convention-api/v1/Convention"
import ConventionRegistration from "models/convention-api/v1/ConventionRegistration"
import Sponsor from "models/convention-api/v1/Sponsor"
import SponsorTier from "models/convention-api/v1/SponsorTier"
import { useRouter } from "next/router"
import React, { useEffect } from "react"
import { pxToRem } from "utils/helpers"
import applyRedirectForAutomaticallyGeneratedConvention
  from 'lib/mw/apply-redirect-for-automatically-generated-convention';

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      padding: "40px",
      [theme.breakpoints.down("lg")]: {
        padding: "40px 20px",
      },
    },
    title: {
      marginBottom: "30px",
    },
  }),
)

export const getServerSideProps = buildServerSideProps(
  applyCache(),
  applyCurrentServerContext(),
  [
    Convention.applyOne("convention", ({ params }) => ({
      filter: {
        id: params.convention_id as string,
      },
      includes: ["convention_ticket_types", "convention_booleans"],
    })),
    Sponsor.applyAll("sponsors"),
    SponsorTier.applyAll("sponsorTiers"),
  ],
  applyRequiredProps("convention", "sponsorTiers", "sponsors"),
  applyRedirectForAutomaticallyGeneratedConvention(),
  applyTitle(({ props }) => props.convention.name),
  applyNextSeo((context) => ({
    openGraph: {
      title: context.props.convention.name,
      url: createURL_SSR(context, {
        path: ROUTE.CONVENTION,
        params: { conventionId: context.props.convention.id },
      }),
      type: "website",
      images: [
        {
          url: context.props.convention.logo_url,
          alt: context.props.convention.name,
        },
      ],
    },
  })),
)

interface ConventionPageProps {
  convention: Convention
  sponsors: Sponsor[]
  sponsorTiers: SponsorTier[]
}

export default buildServerSidePage(function ConventionPage(props: ConventionPageProps) {
  const { sponsorTiers, sponsors, convention: conventionFromServerSide } = props
  const { query } = useRouter()

  const current = useCurrent()
  // @ts-ignore
  const { config: providerConfig } = current

  const { data: convention } = Convention.useOne(() => ({
    filter: {
      id: query.convention_id as string,
    },
    includes: ["convention_ticket_types", "convention_booleans"],
    fallbackData: conventionFromServerSide,
  }))

  const conventionRegistration = ConventionRegistration.useOne(() => ({
    filter: {
      id: query.convention_id as string,
    },
    includes: ["ticket_type", "payment_intent"],
  }))

  const { data: activeVisitorCount } = ActiveVisitorCount.useOne({
    // @ts-ignore
    filter: { convention_convention_id: query.convention_id },
  })

  useEffect(() => {
    // Only for virtual events
    const postActiveUser = async () => {
      try {
        await postActiveAuthenticatedVisitors({
          current,
          // @ts-ignore
          convention_convention_id: convention.id,
        })
      } catch (error) {
        console.error(error)
      }
    }

    if (
      current?.user &&
      convention &&
      convention.location_style === ConventionLocationStyle.VIRTUAL
    ) {
      postActiveUser()
    }
  }, [current, convention])

  const { classes, cx } = useStyles()
  const { classes: headingClasses } = useHeadingStyles()

  if (!convention || (!conventionRegistration.data && conventionRegistration.isValidating)) {
    return <ActivityIndicator />
  }

  const userCount = activeVisitorCount?.count ?? 0

  return (
    <LaunchpadContext.Provider
      value={{
        convention,
        conventionRegistration: conventionRegistration.data,
        userCount,
      }}>
      <MainLayout layout="micro">
        <_Container role="region" aria-label="content">
          <Launchpad />
          {sponsorTiers.length > 0 && sponsors.length > 0 && (
            <div className={classes.root}>
              <Typography variant="h3" className={cx(headingClasses.microh3, classes.title)}>
                Sponsors
              </Typography>
              <SponsorsList sponsorTiers={sponsorTiers} sponsors={sponsors} alignment="left" />
            </div>
          )}
        </_Container>
      </MainLayout>
    </LaunchpadContext.Provider>
  )
})

const _Container = styled(Box)({
  width: "100%",
  height: "100%",
  marginBottom: pxToRem(64),
}) as typeof Box
